/**
 * Spotify Canvas upload (owner request 2026-10-17: "If you connect Spotify for Artists we should
 * have an option to auto-upload Canvas to each song"). Live findings:
 * `docs/music24-connectors/SPOTIFY-CANVAS.md`. Design: `spotify-canvas/` in Claude Design.
 *
 * Music24 uploads the kit Canvas loop (`canvasLoop`) of a song to Spotify with the stored Spotify for
 * Artists sign-in. The record is a kit record of the channel `spotifyCanvas` (`AssetPublishes`, one
 * per kit asset VERSION), so one file goes to Spotify one time. The song page panel "Where it plays"
 * shows the row "Spotify Canvas"; it reads `GET /accounts/:accountId/spotify-canvas`.
 *
 * The web app, the API and the tests read this file. iOS (`SpotifyCanvasRules.swift`) and Android
 * (`SpotifyCanvasRules.kt`) carry a copy of the row rules; change the three together. No imports.
 */

/** The Spotify Canvas file rules (Spotify for Artists): MP4, 9:16, at least 720 px tall, 3 to 8 s, at most 8 MB. */
export const SPOTIFY_CANVAS_FILE_RULES = {
  contentTypes: ['video/mp4'] as readonly string[],
  maxBytes: 8 * 1024 * 1024,
  minSec: 3,
  maxSec: 8,
  /** height / width */
  ratio: 16 / 9,
  ratioTolerance: 0.02,
  minHeightPx: 720,
} as const;

export type SpotifyCanvasFileRefusal = 'type' | 'size' | 'tooShort' | 'tooLong' | 'aspect' | 'tooSmall';

/**
 * Why Spotify refuses the file, or null. A measure that is unknown (null) is not checked: the
 * Music24 render makes 1080 x 1920 H.264 of 4 bars, and an added file was checked at its upload.
 */
export function spotifyCanvasFileRefusal(file: {
  mimeType: string | null;
  sizeBytes: number | null;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
}): SpotifyCanvasFileRefusal | null {
  const rules = SPOTIFY_CANVAS_FILE_RULES;
  if (file.mimeType !== null && !rules.contentTypes.includes(file.mimeType)) {
    return 'type';
  }
  if (file.sizeBytes !== null && file.sizeBytes > rules.maxBytes) {
    return 'size';
  }
  if (file.durationSeconds !== null && file.durationSeconds < rules.minSec - 0.05) {
    return 'tooShort';
  }
  if (file.durationSeconds !== null && file.durationSeconds > rules.maxSec + 0.05) {
    return 'tooLong';
  }
  if (file.width !== null && file.height !== null && file.width > 0) {
    if (Math.abs(file.height / file.width - rules.ratio) > rules.ratio * rules.ratioTolerance) {
      return 'aspect';
    }
    if (file.height < rules.minHeightPx) {
      return 'tooSmall';
    }
  }
  return null;
}

/**
 * The state of the row "Spotify Canvas" in "Where it plays" (design `spotify-canvas` a1):
 * - `notConnected`: no Spotify for Artists sign-in (the row offers Connect).
 * - `reconnect`: the sign-in ended (Reconnect).
 * - `noCanvas`: the kit has no Canvas loop (Make it in the Studio).
 * - `rendering`: the Canvas loop renders.
 * - `noTrack`: the song has no Spotify track link, so Music24 cannot find the track.
 * - `notLive`: the release is not live on Spotify yet. Spotify takes a Canvas only on a live track.
 * - `ready`: the file can go (Upload).
 * - `uploading`: Music24 sends the file, or Spotify processes it.
 * - `inReview`: Spotify reviews the Canvas [guess: the status name was not seen live].
 * - `onSpotify`: Spotify shows the Canvas (`CANVAS_UPLOADED`).
 * - `failed`: the upload failed (`failReason`), with Try again.
 */
export const SPOTIFY_CANVAS_ROW_STATES = [
  'notConnected',
  'reconnect',
  'noCanvas',
  'rendering',
  'noTrack',
  'notLive',
  'ready',
  'uploading',
  'inReview',
  'onSpotify',
  'failed',
] as const;
export type SpotifyCanvasRowState = (typeof SPOTIFY_CANVAS_ROW_STATES)[number];

/**
 * Why a Canvas upload failed:
 * - `reconnect`: Spotify refused the sign-in.
 * - `noPermission`: the login may not edit the Canvas of this track (`canvas.edit.v1`).
 * - `notLive`: Spotify does not show the track as released.
 * - `file`: the file breaks a Canvas rule (`failDetail` = a `SpotifyCanvasFileRefusal`).
 * - `refused`: Spotify answered 4xx (`failDetail` = its text).
 * - `rejected`: Spotify refused the Canvas after the upload (review).
 * - `fileGone`: the file of the kit asset is gone.
 * - `existingCanvas`: an automatic upload found a Canvas on the track that Music24 did not send
 *   (the artist set it in Spotify for Artists). Music24 does not replace it on its own; a click on
 *   "Replace it" sends the Music24 Canvas.
 * - `error`: any other error after the last attempt.
 */
export type SpotifyCanvasFailReason =
  | 'existingCanvas'
  | 'reconnect'
  | 'noPermission'
  | 'notLive'
  | 'file'
  | 'refused'
  | 'rejected'
  | 'fileGone'
  | 'error';

export const SPOTIFY_CANVAS_ERROR_CODES = {
  /** The artist did not accept the Spotify Canvas terms in Music24. Send `acceptTerms: true`. */
  termsRequired: 'M24_CANVAS_TERMS_REQUIRED',
  /** No Spotify for Artists sign-in, or it needs a Reconnect. */
  notConnected: 'M24_PUBLISH_NOT_CONNECTED',
  /** The kit has no ready Canvas loop. */
  notReady: 'M24_CANVAS_NOT_READY',
  /** The song has no Spotify track link. */
  noTrack: 'M24_CANVAS_NO_TRACK',
  /** This Canvas file is on Spotify already, or on its way. */
  alreadyLive: 'M24_PUBLISH_ALREADY_LIVE',
  /** The workspace has no `canUseAutoUpload` (the admin switch is off). */
  autoUploadNotEnabled: 'M24_AUTO_UPLOAD_NOT_ENABLED',
  /** The file breaks a Canvas rule (`reason` = a `SpotifyCanvasFileRefusal`). */
  fileRefused: 'M24_CANVAS_FILE_REFUSED',
} as const;

/** The version of the Canvas terms that the artist accepts (the S4A notice, 2026-10-17). */
export const SPOTIFY_CANVAS_TERMS_VERSION = '2026-10-17';

/** The notice that the artist accepts once (a copy of the Spotify for Artists Canvas notice). */
export const SPOTIFY_CANVAS_TERMS_TEXT =
  'By posting a Canvas, you license it to Spotify under the Spotify for Artists Terms and Conditions. ' +
  'Only post a Canvas on tracks that you own or control, and only with content that you have the rights to use.';

export const SPOTIFY_FOR_ARTISTS_TERMS_URL = 'https://artists.spotify.com/terms';

export interface SpotifyCanvasSettings {
  /** "Upload Canvas to Spotify automatically" (default off). */
  autoUpload: boolean;
  /** ISO time the artist accepted the Canvas terms in Music24, or null. */
  termsAcceptedAt: string | null;
}

/** The Canvas record of the song (the newest `spotifyCanvas` API record of its Canvas loop). */
export interface SpotifyCanvasUpload {
  uuid: string;
  kitAssetUuid: string;
  /** The kit asset version that went (or goes) to Spotify. */
  revision: number;
  status: 'waiting' | 'uploading' | 'scheduled' | 'published' | 'failed' | 'removed' | 'canceled';
  /** The Spotify status as Spotify wrote it (`METADATA_UPLOADED`, `CANVAS_UPLOADED`, ...). */
  spotifyStatus: string | null;
  /** The Spotify canvas id (32 hex) and uri (`spotify:canvas:...`). */
  canvasId: string | null;
  canvasUri: string | null;
  failReason: SpotifyCanvasFailReason | null;
  failDetail: string | null;
  /** True when Music24 started it with the switch, false for a click. */
  auto: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
}

/** `GET /accounts/:accountId/spotify-canvas?catalogItemUuid=` */
export interface SpotifyCanvasSongResponse {
  catalogItemUuid: string;
  state: SpotifyCanvasRowState;
  /** The Spotify for Artists sign-in of the artist. */
  connection: {
    connected: boolean;
    needsReconnect: boolean;
    artistName: string | null;
    avatarUrl: string | null;
  };
  /** The newest Canvas loop of the kit (ready or rendering), or null. */
  canvas: { kitAssetUuid: string; revision: number; ready: boolean; watermark: boolean } | null;
  /** The Spotify track id from the song links, or null. */
  spotifyTrackId: string | null;
  /** The Canvas record of the song (any version), or null. */
  upload: SpotifyCanvasUpload | null;
  /** True when the newest ready Canvas is a newer version than the one on Spotify. */
  newerCanvas: boolean;
  /** The upload is open for the workspace (`canUseAutoUpload`). */
  available: boolean;
  settings: SpotifyCanvasSettings;
}

/** `POST /accounts/:accountId/spotify-canvas` (upload now, try again, or send a newer version). */
export interface StartSpotifyCanvasBody {
  catalogItemUuid: string;
  /** Accepts the Canvas terms (stored with the time) before the first upload. */
  acceptTerms?: boolean;
}

/** `PUT /accounts/:accountId/spotify-canvas/settings` */
export interface UpdateSpotifyCanvasSettingsBody {
  autoUpload: boolean;
  /** Required (true) to turn the switch on when the terms are not accepted yet. */
  acceptTerms?: boolean;
}

export interface SpotifyCanvasSettingsResponse {
  settings: SpotifyCanvasSettings;
}

/**
 * The state of the row. Pure: the API answers it, and the web and native tests read it.
 * Order: the sign-in, the record (its newest state wins while it is on its way or live), the file,
 * the track, the release.
 */
export function spotifyCanvasRowState(input: {
  connected: boolean;
  needsReconnect: boolean;
  canvas: { ready: boolean } | null;
  spotifyTrackId: string | null;
  releasedOnSpotify: boolean;
  upload: Pick<SpotifyCanvasUpload, 'status' | 'spotifyStatus'> | null;
  newerCanvas: boolean;
}): SpotifyCanvasRowState {
  if (input.needsReconnect) {
    return 'reconnect';
  }
  if (!input.connected) {
    return 'notConnected';
  }
  const { upload } = input;
  if (upload !== null && !input.newerCanvas) {
    const fromUpload = uploadRowState(upload);
    if (fromUpload !== null) {
      return fromUpload;
    }
  }
  if (upload !== null && (upload.status === 'waiting' || upload.status === 'uploading')) {
    return 'uploading';
  }
  if (input.canvas === null) {
    return 'noCanvas';
  }
  if (!input.canvas.ready) {
    return 'rendering';
  }
  if (input.spotifyTrackId === null) {
    return 'noTrack';
  }
  if (!input.releasedOnSpotify) {
    return 'notLive';
  }
  return 'ready';
}

/** Words of a Spotify status that say "in review" [guess: not seen live]. */
const REVIEW_WORDS = ['REVIEW', 'MODERAT', 'PENDING'];

function uploadRowState(upload: Pick<SpotifyCanvasUpload, 'status' | 'spotifyStatus'>): SpotifyCanvasRowState | null {
  switch (upload.status) {
    case 'waiting':
    case 'uploading':
    case 'scheduled':
      return 'uploading';
    case 'failed':
      return 'failed';
    case 'published': {
      const status = upload.spotifyStatus ?? '';
      return REVIEW_WORDS.some((word) => status.includes(word)) ? 'inReview' : 'onSpotify';
    }
    case 'removed':
    case 'canceled':
      return null;
  }
}

/** Words of a Spotify status that say "refused" [guess: not seen live]. */
const REJECT_WORDS = ['REJECT', 'FAIL', 'DECLIN', 'REMOV', 'DELET', 'BLOCK'];

/**
 * The record state for a Spotify canvas status. `CANVAS_UPLOADED` is on Spotify (seen live);
 * `METADATA_UPLOADED` waits for the file (seen live). Any other status is mapped by its words.
 */
export function spotifyCanvasStatusOutcome(status: string | null): 'uploading' | 'published' | 'rejected' {
  const value = (status ?? '').toUpperCase();
  if (value === 'CANVAS_UPLOADED') {
    return 'published';
  }
  if (REJECT_WORDS.some((word) => value.includes(word))) {
    return 'rejected';
  }
  if (REVIEW_WORDS.some((word) => value.includes(word))) {
    return 'published';
  }
  return 'uploading';
}

/** `https://open.spotify.com/track/<id>` (with or without `intl-xx/` and a query) or `spotify:track:<id>` → the id. */
export function spotifyTrackIdFromLink(link: string | null | undefined): string | null {
  if (typeof link !== 'string') {
    return null;
  }
  const uri = /^spotify:track:([A-Za-z0-9]{22})$/.exec(link.trim());
  if (uri !== null) {
    return uri[1] ?? null;
  }
  const url = /^https?:\/\/open\.spotify\.com\/(?:intl-[a-z-]+\/)?track\/([A-Za-z0-9]{22})(?:[/?#].*)?$/i.exec(
    link.trim()
  );
  return url === null ? null : (url[1] ?? null);
}
